import { useEffect, useState } from "react";
import { Eye, Plus } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

export const CATEGORY_LABELS = {
  geral: "Geral",
  urgente: "Urgente",
  agenda: "Agenda",
  aluno: "Aluno",
  administrativo: "Administrativo",
  reuniao: "Reunião",
  aviso: "Aviso",
};

export const CategoryBadge = ({ category }) => (
  <Badge variant={category === "urgente" ? "destructive" : "secondary"} data-testid={`category-badge-${category}`}>
    {CATEGORY_LABELS[category] || category}
  </Badge>
);

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");

function AdminView() {
  const [items, setItems] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", category: "geral", audience: "all", selected: [] });
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const { data } = await api.get("/announcements");
    setItems(data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
  }, []);

  const openCreate = async () => {
    setForm({ title: "", body: "", category: "geral", audience: "all", selected: [] });
    try {
      const { data } = await api.get("/announcements/recipients");
      setRecipients(data);
      setDialogOpen(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleRecipient = (id) =>
    setForm((f) => ({
      ...f,
      selected: f.selected.includes(id) ? f.selected.filter((x) => x !== id) : [...f.selected, id],
    }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/announcements", {
        title: form.title,
        body: form.body,
        category: form.category,
        audience: form.audience,
        recipient_ids: form.audience === "selected" ? form.selected : [],
      });
      toast.success(`Comunicado enviado para ${data.recipients_count} professor(es).`);
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (item) => {
    try {
      const { data } = await api.get(`/announcements/${item.id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Comunicados</h1>
          <p className="mt-1 text-sm text-muted-foreground">Envie comunicados aos professores e acompanhe a leitura.</p>
        </div>
        <Button onClick={openCreate} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="announcement-add-button">
          <Plus size={16} className="mr-2" /> Novo comunicado
        </Button>
      </div>

      <div className="border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider">Título</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Categoria</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider md:table-cell">Enviado por</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider lg:table-cell">Data</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Leitura</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="p-6 text-sm text-muted-foreground" data-testid="announcements-empty">
                  Nenhum comunicado enviado.
                </TableCell>
              </TableRow>
            )}
            {items.map((a) => (
              <TableRow key={a.id} className="transition-colors hover:bg-zinc-50" data-testid={`announcement-row-${a.id}`}>
                <TableCell className="font-medium">{a.title}</TableCell>
                <TableCell><CategoryBadge category={a.category} /></TableCell>
                <TableCell className="hidden md:table-cell">{a.created_by_name}</TableCell>
                <TableCell className="hidden lg:table-cell">{fmtDateTime(a.created_at)}</TableCell>
                <TableCell data-testid={`announcement-read-count-${a.id}`}>
                  {a.read_count}/{a.recipients_count} lido(s)
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" title="Detalhes" onClick={() => openDetail(a)} data-testid={`announcement-view-${a.id}`}>
                    <Eye size={16} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="announcement-dialog">
          <DialogHeader>
            <DialogTitle>Novo comunicado</DialogTitle>
            <DialogDescription>Preencha o título, a mensagem e escolha os destinatários.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input required minLength={3} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="announcement-title-input" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="announcement-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Destinatários</Label>
                <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                  <SelectTrigger data-testid="announcement-audience-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os professores ativos</SelectItem>
                    <SelectItem value="selected">Professores selecionados</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.audience === "selected" && (
              <div className="space-y-2 border border-border bg-zinc-50 p-3" data-testid="announcement-recipients-picker">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Selecionar professores</p>
                {recipients.length === 0 && (
                  <p className="text-xs text-muted-foreground">Nenhum professor ativo com usuário cadastrado.</p>
                )}
                {recipients.map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.selected.includes(r.id)}
                      onCheckedChange={() => toggleRecipient(r.id)}
                      data-testid={`announcement-recipient-${r.id}`}
                    />
                    {r.name}
                  </label>
                ))}
              </div>
            )}
            <div className="space-y-2">
              <Label>Mensagem *</Label>
              <Textarea required minLength={3} rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="announcement-body-input" />
            </div>
            <Button
              type="submit"
              disabled={saving || (form.audience === "selected" && form.selected.length === 0)}
              className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90"
              data-testid="announcement-save-button"
            >
              {saving ? "Enviando..." : "Enviar comunicado"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="announcement-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.title}</DialogTitle>
            <DialogDescription>Detalhes do comunicado e status de leitura dos destinatários.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Categoria</p><CategoryBadge category={detail.category} /></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Enviado por</p><p className="font-medium">{detail.created_by_name}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Data</p><p className="font-medium">{fmtDateTime(detail.created_at)}</p></div>
              </div>
              <p className="whitespace-pre-wrap border border-border bg-zinc-50 p-4 text-sm" data-testid="announcement-detail-body">{detail.body}</p>
              <div>
                <h3 className="font-heading text-base font-semibold">Destinatários</h3>
                <div className="mt-2 space-y-2" data-testid="announcement-recipients-list">
                  {detail.recipients.map((r) => (
                    <div key={r.user_id} className="flex items-center justify-between border border-border p-3 text-sm" data-testid={`announcement-recipient-status-${r.user_id}`}>
                      <span className="font-medium">{r.name}</span>
                      {r.read_at ? (
                        <Badge className="bg-primary text-primary-foreground">Lido em {fmtDateTime(r.read_at)}</Badge>
                      ) : (
                        <Badge variant="secondary">Não lido</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InboxView() {
  const [data, setData] = useState({ unread_count: 0, items: [] });
  const [reading, setReading] = useState(null);

  const load = async () => {
    const { data: d } = await api.get("/announcements/inbox");
    setData(d);
  };

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
  }, []);

  const openItem = async (item) => {
    setReading(item);
    if (!item.read_at) {
      try {
        await api.post(`/announcements/${item.id}/read`);
        window.dispatchEvent(new Event("announcements-updated"));
        load().catch(() => {});
      } catch (err) {
        toast.error(formatApiError(err));
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="inbox-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Comunicados</h1>
          <p className="mt-1 text-sm text-muted-foreground">Comunicados enviados pela administração do IMJ.</p>
        </div>
        {data.unread_count > 0 && (
          <Badge className="bg-primary px-3 py-1.5 text-sm text-primary-foreground" data-testid="inbox-unread-count">
            {data.unread_count} não lido(s)
          </Badge>
        )}
      </div>

      <div className="space-y-2" data-testid="inbox-list">
        {data.items.length === 0 && (
          <p className="border border-border bg-white p-6 text-sm text-muted-foreground" data-testid="inbox-empty">
            Nenhum comunicado recebido.
          </p>
        )}
        {data.items.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => openItem(a)}
            className={`flex w-full flex-wrap items-center gap-3 border bg-white p-4 text-left transition-colors hover:border-primary ${
              a.read_at ? "border-border" : "border-l-4 border-l-primary border-border"
            }`}
            data-testid={`inbox-item-${a.id}`}
          >
            <div className="min-w-0 flex-1">
              <p className={`text-sm ${a.read_at ? "font-normal" : "font-bold"}`}>{a.title}</p>
              <p className="text-xs text-muted-foreground">
                {a.created_by_name} · {fmtDateTime(a.created_at)}
              </p>
            </div>
            <CategoryBadge category={a.category} />
            {!a.read_at && <Badge className="bg-primary text-primary-foreground">Novo</Badge>}
          </button>
        ))}
      </div>

      <Dialog open={!!reading} onOpenChange={() => setReading(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="inbox-read-dialog">
          <DialogHeader>
            <DialogTitle>{reading?.title}</DialogTitle>
            <DialogDescription>Comunicado enviado pela administração do IMJ.</DialogDescription>
          </DialogHeader>
          {reading && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <CategoryBadge category={reading.category} />
                <span>{reading.created_by_name} · {fmtDateTime(reading.created_at)}</span>
              </div>
              <p className="whitespace-pre-wrap border border-border bg-zinc-50 p-4 text-sm" data-testid="inbox-read-body">
                {reading.body}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Announcements() {
  const { user } = useAuth();
  return user.role === "professor" ? <InboxView /> : <AdminView />;
}
